import { Box, Button, FormikTextInput, Modal, toast } from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { OrganizationInvoiceSettingV2DisplayMap, OrganizationInvoiceSettingsV2 } from 'types';

const invoiceSettingsSchema = yup.object({
  invoiceDueDays: yup
    .number()
    .min(0, 'Invoice Due Days must be at least 0')
    .max(60, 'Invoice Due Days cannot exceed 60 days')
    .required('Invoice Due Days is required'),
  installApprovalPauseDays: yup
    .number()
    .min(0, 'Install Approval Pause Days must be at least 0')
    .required('Install Approval Pause Days is required'),
  materialsEstimatePauseDays: yup
    .number()
    .min(0, 'Materials Estimate Pause Days must be at least 0')
    .required('Materials Estimate Pause Days is required'),
});

interface InvoiceSettingsEditModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: any) => Promise<void>;
  currentInvoiceSettings?: OrganizationInvoiceSettingsV2;
  modalId: string;
  modalTitle: string;
  submitButtonText: string;
  refetchInvoiceSettings: () => void;
}

export const InvoiceSettingsEditModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  currentInvoiceSettings,
  modalId,
  modalTitle,
  submitButtonText = 'Save Changes',
  refetchInvoiceSettings,
}: InvoiceSettingsEditModalProps) => {
  const initialValues = useMemo(() => {
    return {
      invoiceDueDays: currentInvoiceSettings?.invoiceDueDays || 0,
      installApprovalPauseDays: currentInvoiceSettings?.installApprovalPauseDays || 0,
      materialsEstimatePauseDays: currentInvoiceSettings?.materialsEstimatePauseDays || 0,
    };
  }, [currentInvoiceSettings]);

  const showErrorToast = (message: string) => {
    toast.error(message, {
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
      },
    });
  };

  const onSubmit = async (values: any, { setSubmitting, resetForm }: FormikHelpers<any>) => {
    try {
      await handleSubmit({ ...values });
      resetForm();
      handleClose();
      refetchInvoiceSettings();
      toast.success('Invoice settings updated successfully');
    } catch (error: any) {
      setSubmitting(false);
      showErrorToast(`Failed to update Invoice settings: ${error.data.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const renderEventSchedule = useCallback(
    (isSubmitting: boolean) => {
      return (
        <Box childGap={{ base: 'lg', desktop: 'xl' }}>
          {Object.keys(initialValues).map((key) => (
            <Box key={key} direction="row" justifyContent="space-between" alignItems="center" gap="md">
              <Box>
                {OrganizationInvoiceSettingV2DisplayMap[key as keyof typeof OrganizationInvoiceSettingV2DisplayMap]}
              </Box>
              <Box width="lg" textAlign={{ base: 'left', desktop: 'right' }}>
                <Field type="number" isDisabled={isSubmitting} name={key} id={key} component={FormikTextInput} />
              </Box>
            </Box>
          ))}
        </Box>
      );
    },
    [initialValues],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={invoiceSettingsSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) {
                resetForm();
                handleClose();
              }
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                <Box borderWidth="xs" borderColor="grey-100" padding="md" radius="md">
                  <FieldArray name="invoiceSchedule" render={() => renderEventSchedule(isSubmitting)} />
                </Box>
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
